import { BasicAuthenticationClient } from '@frontend/authentication-v2';
import { MobileWizardStateData, MobileWizardStateRecord, clearData, updateData } from '@frontend/wizard/mobile';
import { ScreenTrigger, ScreenType, Step } from '@frontend/wizard/types';
import { Action, ThunkDispatch } from '@reduxjs/toolkit';
import { NavigateFunction, useParams } from 'react-router-dom';

import { triggerActivity } from '../../kiosk-idle-timer/kiosk-idle-timer.controller';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { UserInterfaceNavigationState, endUserSession } from '../../redux/terminal-navigation-slice';
import { useKioskNavigate } from '../../utils/kiosk-navigation';

export interface ScreenProps {
    navigationState: UserInterfaceNavigationState;
    dispatch: ThunkDispatch<any, any, Action>;
    wizardId: string | undefined;
    store: MobileWizardStateRecord | null;
    wizardData: MobileWizardStateData;
    updateWizardData: (data: Partial<MobileWizardStateData>) => void;
    screen_type: string | undefined;

    //Navigation
    navigate: NavigateFunction;
    finalStep: boolean;
    onSubmit: (nextStep?: Step) => void;
    onHome: () => void;
    onLogout: () => void;
    onContinue: () => void;
    continueEnabled: boolean;
    onBack: () => void;
    backEnabled: boolean;
}

export const useScreen = (): ScreenProps => {
    const navigationState = useAppSelector((store) => store.navigation);
    const navigate = useKioskNavigate();
    const dispatch = useAppDispatch();
    const { wizardId, screen_type } = useParams<{ wizardId: string; screen_type?: ScreenType }>();
    const wizardStore = useAppSelector((state) => state.wizards);
    const store = wizardId ? wizardStore[wizardId] : null;

    const updateWizardData = (data: Partial<MobileWizardStateData>) => {
        if (store && wizardId) {
            dispatch(
                updateData({
                    key: wizardId,
                    data: { ...data }
                })
            );
        }
    };

    const clearWizardCache = () => {
        if (wizardId) {
            dispatch(clearData(wizardId));
        }
    };

    const submitStep = store?.steps.find((step) => step.screen === screen_type && [ScreenTrigger.SUBMIT].includes(step.trigger));
    const onSubmit = (nextStep?: Step) => {
        triggerActivity();
        if (nextStep) {
            if (nextStep.next_screen === ScreenType.HOME) onHome();
            else navigate(`../${nextStep.next_screen}`);
            return;
        }
        if (submitStep) {
            if (submitStep.next_screen === ScreenType.HOME) onHome();
            else navigate(`../${submitStep.next_screen}`);
            return;
        }
        onLogout();
    };

    const onHome = () => {
        store?.from ? navigate(store.from) : navigate('/');
    };
    const onLogout = () => {
        //TODO add multi user logout handling
        dispatch(endUserSession());
        if (navigationState.user?.auth.jwt_token) {
            BasicAuthenticationClient.logout(navigationState.user.auth.jwt_token);
        }
        onHome();
    };
    const backStep = store?.steps.find((step) => step.screen === screen_type && [ScreenTrigger.BACK, ScreenTrigger.BACK_TO].includes(step.trigger));
    const onBack = () => {
        if (backStep) {
            triggerActivity();
            if (backStep.trigger === ScreenTrigger.BACK) {
                if (backStep.screen === store?.wizard.start_screen) onHome();
                else navigate(-1);
                return;
            } else if (backStep.trigger === ScreenTrigger.BACK_TO) {
                if (backStep.next_screen === ScreenType.HOME) onHome();
                else if (backStep.next_screen === ScreenType.START_SCREEN && store?.wizard.start_screen) navigate(`../${store?.wizard.start_screen}`);
                else if (backStep.next_screen === ScreenType.PREVIOUS) {
                    if (backStep.screen === store?.wizard.start_screen) onHome();
                    else navigate(-1);
                    return;
                } else navigate(`../${backStep.next_screen}`);
                return;
            }
        }
    };

    const continueStep = store?.steps.find((step) => step.screen === screen_type && step.trigger === ScreenTrigger.CONTINUE_TO);
    const onContinue = () => {
        if (continueStep) {
            triggerActivity();
            clearWizardCache();
            if (continueStep.next_screen === ScreenType.HOME) onHome();
            else if (continueStep.next_screen === ScreenType.START_SCREEN && store?.wizard.start_screen) navigate(`../${store?.wizard.start_screen}`);
            else if (continueStep.next_screen === ScreenType.PREVIOUS) {
                if (continueStep.screen === store?.wizard.start_screen) onHome();
                else navigate(-1);
            } else navigate(`../${continueStep.next_screen}`);
        }
    };

    return {
        navigationState,
        dispatch,
        wizardId,
        store,
        updateWizardData,
        wizardData: store?.data ?? ({} as MobileWizardStateData),
        screen_type,

        //Navigation
        navigate,
        finalStep: !submitStep, //just a hint to know if we should expect further navigation or exit on submit
        onSubmit,
        onHome,
        onLogout,
        onContinue,
        continueEnabled: !!continueStep,
        onBack,
        backEnabled: !!backStep
    };
};
